import { useCallback, useEffect, useState } from 'react';
import { PanelsTopLeft, type LucideIcon } from 'lucide-react';
import { DEFAULT_PAGE_ICON, pageIconComponentName } from '@featit/shared/page-icons';
import { accentIconProps } from '@/components/accentIcon';

export type PageIconSet = Record<string, LucideIcon>;

let loadedIconSet: PageIconSet | null = null;
/** `isPageIconName`, from the icon set's chunk: the names list stays out of the entry with it. */
let loadedIsPageIconName: ((value: unknown) => boolean) | null = null;
let pendingIconSet: Promise<PageIconSet> | null = null;

/** Fetches the full icon set once per session; a failed fetch may be retried by the next caller. */
export function loadPageIconSet(): Promise<PageIconSet> {
  pendingIconSet ??= import('./pageIconSet')
    .then((module) => {
      loadedIconSet = module.icons;
      loadedIsPageIconName = module.isPageIconName;
      return loadedIconSet;
    })
    .catch((cause) => {
      pendingIconSet = null;
      throw cause;
    });
  return pendingIconSet;
}

type IconSetState =
  | { status: 'ready'; icons: PageIconSet; retry: () => void }
  | { status: 'loading' | 'failed'; retry: () => void };

/** The icon set, loading it only when `needed`; `retry` tries again after a failed fetch. */
export function usePageIconSet(needed: boolean): IconSetState {
  const [status, setStatus] = useState<'ready' | 'loading' | 'failed'>(() =>
    loadedIconSet ? 'ready' : 'loading',
  );
  const retry = useCallback(() => setStatus('loading'), []);
  useEffect(() => {
    if (!needed || status !== 'loading') return undefined;
    let active = true;
    loadPageIconSet().then(
      () => active && setStatus('ready'),
      () => active && setStatus('failed'),
    );
    return () => {
      active = false;
    };
  }, [needed, status]);
  return status === 'ready' && loadedIconSet
    ? { status, icons: loadedIconSet, retry }
    : { status: status === 'ready' ? 'loading' : status, retry };
}

/**
 * A page's icon, identical wherever the page is shown (F-0186, P-8).
 *
 * `null`, an unknown name, and an icon set that failed to load all draw the default. While the set
 * loads, the slot is an empty box of the same size — nothing shifts, and no wrong icon flashes.
 * Whether a name is known is read from the set's own chunk, so an unknown name waits for it too.
 * `data-page-icon` names what was drawn, for tests and for anyone inspecting the page.
 *
 * `accentPosition`: where the icon stands in a navigation list (the sidebar's page tree), so it takes
 * that place's frame stop in Colour (F-0579 ACC-4a). Unset, as on a page's own header, it is plain.
 */
export function PageIcon({
  name,
  className = 'h-4 w-4',
  accentPosition,
}: {
  name: string | null | undefined;
  className?: string;
  accentPosition?: number;
}) {
  const chosen = name && name !== DEFAULT_PAGE_ICON ? name : null;
  const iconSet = usePageIconSet(chosen !== null);
  if (chosen && iconSet.status === 'loading') {
    return (
      <span
        aria-hidden="true"
        data-page-icon-loading={chosen}
        className={`inline-block shrink-0 ${className}`}
      />
    );
  }
  const Icon =
    chosen && iconSet.status === 'ready' && loadedIsPageIconName?.(chosen)
      ? (iconSet.icons[pageIconComponentName(chosen)] ?? PanelsTopLeft)
      : PanelsTopLeft;
  const drawn = Icon === PanelsTopLeft ? DEFAULT_PAGE_ICON : chosen;
  return (
    <Icon
      aria-hidden="true"
      data-page-icon={drawn}
      className={`shrink-0 ${className}`}
      {...(accentPosition === undefined ? {} : accentIconProps(accentPosition))}
    />
  );
}
